import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useWalletStore } from '@/store/wallet';
import { useUiStore } from '@/store/ui';
import { formatDpb } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';

/** 余额展示：微单位 → DPB（保留 4 位小数，bigint 安全）。 */
const showDpb = (micro: string): string => formatDpb(micro, 4);

/** 钱包：余额 / 水龙头 / 转账 / 交易历史。 */
export function WalletPage(): JSX.Element {
  const balanceMicro = useWalletStore((s) => s.balanceMicro);
  const txs = useWalletStore((s) => s.txs);
  const faucetClaimed = useWalletStore((s) => s.faucetClaimed);
  const registered = useIdentityStore((s) => s.registered);
  const faucetClaimedMeta = useIdentityStore((s) => s.faucetClaimed);

  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = async (): Promise<void> => {
    try {
      await container.wallet.getBalance();
      await container.wallet.history();
    } catch (err) {
      // getBalance / history 可能因 E_NOT_IMPLEMENTED 失败（链客户端未就绪或 MockChain 不支持某些方法）。
      // 此时 UI 应静默展示 balanceMicro='0'，不抛 Toast 以免干扰用户。
      if (err instanceof MeshLinkError && err.code === 'E_NOT_IMPLEMENTED') return;
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claim = async (): Promise<void> => {
    setBusy(true);
    try {
      await container.wallet.claimFaucet();
      useUiStore.getState().pushToast('success', '新手奖励已到账');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  const register = async (): Promise<void> => {
    setBusy(true);
    try {
      await container.wallet.registerDid();
      useUiStore.getState().pushToast('success', 'DID 已注册');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  const transfer = async (): Promise<void> => {
    if (!to.trim() || !amount.trim()) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setBusy(true);
    try {
      await container.wallet.transfer(to, amount);
      setTo('');
      setAmount('');
      useUiStore.getState().pushToast('success', '转账已提交');
      await refresh();
    } catch (err) {
      const code = err instanceof MeshLinkError ? err.code : 'E_UNKNOWN';
      // 仅对 Nonce 冲突注入重试处理器（catalog action='retry'）；其它码保留内置路由（如 topup/switchRelay），
      // 避免 onAction 越过内置路由造成「按钮文案与行为不一致」。
      useUiStore.getState().pushError(
        code,
        code === 'E_NONCE_CONFLICT' ? { onAction: () => void transfer() } : undefined,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className="card p-5">
        <h2 className="text-sm font-medium text-text">余额</h2>
        <p className="mt-1 text-2xl font-semibold text-text">{showDpb(balanceMicro)} DPB</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge tone={registered ? 'ok' : 'warn'}>{registered ? 'DID 已注册' : 'DID 未注册'}</Badge>
          <Badge tone={faucetClaimed || faucetClaimedMeta ? 'gold' : 'neutral'}>
            {faucetClaimed || faucetClaimedMeta ? '已领新手奖励' : '未领新手奖励'}
          </Badge>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {registered ? null : (
            <Button variant="outline" size="sm" loading={busy} onClick={() => void register()}>
              注册 DID
            </Button>
          )}
          {faucetClaimed || faucetClaimedMeta ? null : (
            <Button variant="outline" size="sm" loading={busy} onClick={() => void claim()}>
              领取新手奖励
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => void refresh()}>
            刷新
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">转账</h2>
        <div className="space-y-3">
          <Input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="收款地址 0x…"
          />
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="金额（DPB），如 1.5"
          />
          <Button size="sm" loading={busy} onClick={() => void transfer()}>
            转账
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">交易记录</h2>
        {txs.length === 0 ? (
          <EmptyState title="暂无交易" description="领取新手奖励后即可看到记录" />
        ) : (
          <ul className="space-y-2">
            {txs.map((t) => (
              <li
                key={t.txHash}
                className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs"
              >
                <span className="text-text">{t.type}</span>
                <span className="text-dim">
                  {t.amountMicro ? `${showDpb(t.amountMicro)} DPB` : '—'}
                </span>
                <span className="mono text-dim">#{t.blockHeight}</span>
                <span className="text-dim">{new Date(t.timestamp).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default WalletPage;
